import { Box, Button, Flex, LoadingOverlay, Text, Title, UnstyledButton } from "@dehub/theme";
import { PencilSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import bz from "@/features/business/business.module.css";
import { BranchApi, WarehouseApi } from "@/apis";
import { WarehouseStaffTab } from "@/features/warehouse-staff";
import type { IWarehouseListRow } from "@/models/Warehouse";
import { ReasonTooltip } from "@/shared/a11y/reason-tooltip";
import { MissingPermission } from "@/shared/a11y/trans-code";
import { PageLayout } from "@/shared/layout";
import HttpHandler from "@/utils/http-handler";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { WarehouseForm, type IWarehouse, type WarehouseFormHandle } from "./components/forms/warehouse-form";
import { WarehouseView } from "./components/WarehouseView";
import { useWarehousePermissions } from "./hooks/useWarehousePermissions";
import classes from "./warehouse.module.css";

type TabValue = "info" | "staff";
type Loaded = IWarehouse & IWarehouseListRow;

export function WarehouseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("warehouse");
  const { t: tCommon } = useTranslation("common");
  const perms = useWarehousePermissions();

  // Edit needs both permissions: the page reads with GET (PRT_WRH_DETAIL) and saves with PUT (PRT_WRH_EDIT).
  const canEdit = perms.edit.hasAccess && perms.detail.hasAccess;
  const editMissing = [!perms.edit.hasAccess ? perms.codes.edit : null, !perms.detail.hasAccess ? perms.codes.detail : null].filter(Boolean).join(", ");
  const isEditMode = searchParams.get("mode") === "edit" && canEdit;
  const activeTab: TabValue = searchParams.get("tab") === "staff" ? "staff" : "info";

  const [warehouse, setWarehouse] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const formHandle = useRef<WarehouseFormHandle | null>(null);

  const { data: branches } = useSWR(
    id && perms.detail.hasAccess ? "/branch" : null,
    () => BranchApi.list({ filter: { query: "" }, offset: { page: 1, limit: 100 } }),
    { revalidateOnFocus: false },
  );

  useEffect(() => {
    if (!id || !perms.detail.hasAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    WarehouseApi.get(id)
      .then((data) => setWarehouse(data as Loaded))
      .catch((err) => message.error((err as HttpHandler)?.message ?? tCommon("error")))
      .finally(() => setLoading(false));
  }, [id, perms.detail.hasAccess, tCommon]);

  const reload = async () => {
    if (!id) return;
    try {
      setWarehouse((await WarehouseApi.get(id)) as Loaded);
    } catch (err) {
      message.error((err as HttpHandler)?.message ?? tCommon("error"));
    }
  };

  const setParams = (patch: Record<string, string | null>) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );

  const active = warehouse?.warehouseStatus === "ACTIVE";
  const branchName = useMemo(() => branches?.rows?.find((b: { id: string }) => b.id === warehouse?.branchId)?.name, [branches, warehouse?.branchId]);
  const [liveStaffCount, setLiveStaffCount] = useState<number | null>(null);
  const staffCount = liveStaffCount ?? warehouse?.staffs?.length ?? 0;

  const breadcrumb = [
    { to: "/", label: tCommon("home") },
    { to: "/our-warehouse", label: t("pageTitle") },
    { label: warehouse?.name || warehouse?.refCode || "-" },
  ];

  const headerActions = !warehouse
    ? []
    : isEditMode
      ? [
          <Button key="cancel" variant="default" disabled={saving} onClick={() => setParams({ mode: null })}>
            {tCommon("actions.cancel")}
          </Button>,
          ...(active
            ? []
            : [
                <Button key="activate" variant="default" disabled={saving} onClick={() => formHandle.current?.activate()}>
                  {t("detail.saveAndActivate")}
                </Button>,
              ]),
          <Button key="save" loading={saving} onClick={() => formHandle.current?.save()}>
            {tCommon("actions.save")}
          </Button>,
        ]
      : activeTab === "info"
        ? [
            <ReasonTooltip key="edit" reason={canEdit ? null : <MissingPermission code={editMissing} />}>
              <Button variant="default" leftSection={<PencilSimpleIcon size={16} />} onClick={() => setParams({ mode: "edit" })}>
                {tCommon("actions.edit")}
              </Button>
            </ReasonTooltip>,
          ]
        : [];

  if (!perms.detail.hasAccess) {
    return <PageLayout breadcrumb={breadcrumb.slice(0, 2)} title={t("pageTitle")}>{perms.detail.accessDenied()}</PageLayout>;
  }

  return (
    <PageLayout
      breadcrumb={breadcrumb}
      title={
        <Box component="span" className={classes.titleRow}>
          <Box component="span">{warehouse?.name ?? "-"}</Box>
          {warehouse?.refCode ? (
            <Text component="span" className={classes.code} translate="no">
              {warehouse.refCode}
            </Text>
          ) : null}
          {warehouse ? (
            <Box component="span" className={`${bz.pill} ${active ? bz.pillActive : ""}`}>
              <Box component="span" className={bz.pillDot} />
              {tCommon(`status.${active ? "ACTIVE" : "INACTIVE"}`)}
            </Box>
          ) : null}
          {warehouse?.isDefault ? (
            <Box component="span" className={classes.chip}>
              {t("detail.mainWarehouse")}
            </Box>
          ) : null}
        </Box>
      }
      subTitle={
        warehouse
          ? t("detail.subtitle", { branch: branchName ?? "-", user: warehouse.regUser ? renderName(warehouse.regUser) : "-" })
          : undefined
      }
      extra={headerActions}>
      <Box pos="relative" mih={400}>
        <LoadingOverlay visible={loading} />
        {warehouse ? (
          <Flex direction="column" gap="md">
            <Box role="tablist" aria-label={t("detail.tabsLabel")} className={classes.detailTabs}>
              <UnstyledButton role="tab" aria-selected={activeTab === "info"} className={classes.detailTab} onClick={() => setParams({ tab: null })}>
                {tCommon("tab.mainInfo")}
              </UnstyledButton>
              <UnstyledButton role="tab" aria-selected={activeTab === "staff"} className={classes.detailTab} onClick={() => setParams({ tab: "staff", mode: null })}>
                {t("tab.staff")}
                <Box component="span" className={classes.tabCount}>
                  {staffCount}
                </Box>
              </UnstyledButton>
            </Box>

            {activeTab === "info" ? (
              isEditMode ? (
                <WarehouseForm
                  branches={branches}
                  warehouse={warehouse}
                  hideActions
                  handleRef={formHandle}
                  onBusyChange={setSaving}
                  reload={reload}
                  onClose={() => setParams({ mode: null })}
                />
              ) : (
                <WarehouseView warehouse={warehouse} branches={branches} />
              )
            ) : (
              <WarehouseStaffTab warehouseId={warehouse.id} readOnly={false} onCountChange={setLiveStaffCount} />
            )}
          </Flex>
        ) : null}
        {!warehouse && !loading ? (
          <Title order={2} fz="var(--mantine-font-size-lg)">
            {t("detail.notFound")}
          </Title>
        ) : null}
      </Box>
    </PageLayout>
  );
}
